<template>
  <b-container class="mw-100 px-0">
    <template v-if="hasReport">
      <component
        v-if="nativeComponent"
        :is="nativeComponent"
        ref="nativeReportRef"
      />
      <PowerBIReport
        v-else-if="decodedReportHash.reportID"
        :id="decodedReportHash.reportID"
        :company-id="decodedReportHash.companyID"
        class="w-100"
      />
    </template>
    <div v-else class="text-center">
      <span class="h3 mb-3 d-block">No reports assigned.</span>
      <ContactAdminButton />
    </div>
  </b-container>
</template>

<script>
import Vue from 'vue'
import { defineComponent, ref, computed, watch, onMounted } from '@vue/composition-api'
import { createVuexHelpers } from 'vue2-helpers'
import { useRoute, useRouter } from 'vue2-helpers/vue-router'

import { ReportsService } from '@services'

import PowerBIReport from '@components/PowerBIReport'
import ContactAdminButton from '@components/ContactAdminButton'
import RecProductionReport from '@components/RecProductionReport'
import BDXValidationReport from '@components/BDXValidationReport'

// LIQ-2339/LIQ-2340: maps a Reports row's own native_component (set server-side, see
// company.models.Reports.native_component) to the Vue component Dashboard mounts instead of
// PowerBIReport - explicit dispatch table rather than string-matching a report's display name
// (a prior attempt at this exact integration, isFCPProductionReportName, did that and was later
// reverted).
const NATIVE_REPORT_COMPONENTS = {
  'rec-production-report': RecProductionReport,
  'bdx-validation-report': BDXValidationReport
}

export default defineComponent({
  name: 'Dashboard',
  components: {
    PowerBIReport,
    ContactAdminButton,
    RecProductionReport,
    BDXValidationReport
  },
  // Lives here (not on RecProductionReport itself), same reasoning as Reconciliation.vue's own
  // identical guard: vue-router only calls in-component navigation guards on the actual
  // route-matched component, which for /dashboard/:report_id? is this page, not whichever native
  // component it happens to mount. Guarded with a typeof check since not every native component
  // is guaranteed to expose guardUnsavedChanges.
  beforeRouteLeave(to, from, next) {
    const nativeRef = this.$refs.nativeReportRef

    if (nativeRef && 'function' === typeof nativeRef.guardUnsavedChanges && nativeRef.guardUnsavedChanges(() => next())) return

    next()
  },
  setup() {
    const route = useRoute()
    const router = useRouter()

    const { useGetters } = createVuexHelpers()
    const { user } = useGetters(['user'])

    const reportId = ref(null)

    const hasReport = computed(() => Boolean(reportId.value))

    const decodedReportHash = computed(() => {
      let reportID = 'number' === typeof reportId.value ? reportId.value : null
      let companyID = null

      if (!reportId.value || 'number' === typeof reportId.value) {
        return { reportID, companyID }
      }

      let decodeStr = atob(atob(reportId.value))
      let replaceStr = ':report-id'

      if (decodeStr) {
        if (decodeStr.includes(':company-')) {
          replaceStr = decodeStr.slice(decodeStr.indexOf(':'))

          companyID = parseInt(decodeStr.slice(decodeStr.indexOf('-') + 1))
        }

        reportID = parseInt(decodeStr.replace(replaceStr, ''))
      }

      return {
        reportID,
        companyID
      }
    })

    // LIQ-2339: the assigned-reports list (already fetched here as a fallback when no report id
    // was otherwise resolved) also carries each report's own is_native/native_component fields
    // (see ShortReportSerializer/ShortReportCompanyListSerializer/ShortReportMemberListSerializer
    // on the backend) - reused as the native-detection source below instead of a separate call,
    // so this adds one cheap local-DB list query and zero extra Power BI/Azure calls.
    const reportsMeta = ref([])

    const matchedReport = computed(() => {
      const targetId = decodedReportHash.value.reportID
      if (!targetId) return null
      return reportsMeta.value.find((r) => (r.report_id || r.id) === targetId) || null
    })

    const nativeComponent = computed(() => {
      if (!matchedReport.value || !matchedReport.value.is_native) return null
      return NATIVE_REPORT_COMPONENTS[matchedReport.value.native_component] || null
    })

    watch(
      () => route.params,
      async (newParams) => {
        const { report_id } = newParams || {}

        if (report_id) reportId.value = report_id
        else if (user.value)
          reportId.value =
            user.value.report_id ||
            (user.value.company ? user.value.company.default_report_id : null)

        try {
          const {
            data: {
              data: { data: reports }
            }
          } = await ReportsService.list(null, false)

          reportsMeta.value = reports || []

          if (!reportId.value && 0 < reports.length)
            reportId.value = reports[0].id || reports[0].report_id
        } catch (_) {}
      },
      {
        deep: true,
        immediate: true
      }
    )

    watch(
      route.query,
      (newQueries) => {
        if (newQueries.hasOwnProperty('manage-bdx')) {
          router.push({ name: 'ManageBDX' })
        }
      },
      { immediate: true }
    )

    watch(
      route.query,
      (newQueries) => {
        if (newQueries.hasOwnProperty('cash-call')) {
          router.push({ name: 'CashCallDetails' })
        }
      },
      { immediate: true }
    )

    // Access-denied redirect from a superuser-only route (e.g. /invoicing,
    // see router.js requiresSuperuser guard). Read once the Dashboard has
    // mounted and the route has settled — a toast fired from inside the
    // guard mid-navigation, or from an immediate query watcher, doesn't
    // render reliably. Strip the flag afterwards so a refresh doesn't
    // re-trigger it.
    onMounted(() => {
      // Access-denied redirect from a superuser-only route (e.g. /invoicing,
      // see router.js requiresSuperuser guard). The guard stashes the flag in
      // sessionStorage (a query param wouldn't survive this page's own
      // /dashboard/:report_id re-navigation). Read + clear it here so a
      // refresh doesn't re-show the toast.
      let deniedPage = null
      try {
        deniedPage = window.sessionStorage.getItem('accessDeniedPage')
        if (deniedPage) window.sessionStorage.removeItem('accessDeniedPage')
      } catch (e) {}

      if (deniedPage) {
        Vue.toasted.show('You do not have access to this page.', { duration: 4000 })
      }
    })

    return {
      reportId,
      hasReport,
      decodedReportHash,
      nativeComponent
    }
  }
})
</script>
